import { useEffect, useState } from "react";
import { Search, Download, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";

export const BookingsView = ({ events }) => {
  const [eventId, setEventId] = useState("");
  const [q, setQ] = useState("");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/bookings", { params: { event_id: eventId || undefined, q: q || undefined } });
      setBookings(data);
    } catch { toast.error("Failed to load bookings"); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eventId]);

  const exportCsv = () => {
    const token = localStorage.getItem("sirakh_token");
    const url = `${API}/admin/bookings/export?token=${token}${eventId ? `&event_id=${eventId}` : ""}`;
    window.open(url, "_blank");
  };

  const deleteTicketData = async () => {
    try {
      const { data } = await api.delete(`/admin/events/${confirmDel}/bookings`);
      toast.success(`Deleted ${data.deleted} bookings`);
      setConfirmDel(null);
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="bookings-view">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <select className="input-field sm:max-w-xs" value={eventId} onChange={(e) => setEventId(e.target.value)} data-testid="bookings-event-filter">
          <option value="">All events</option>
          {events.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
        <div className="flex flex-1 gap-2">
          <input className="input-field" placeholder="Search name / mobile / booking ID" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="bookings-search-input" />
          <button onClick={load} className="btn-outline !px-4" data-testid="bookings-search-button"><Search size={16} /></button>
        </div>
        <button onClick={exportCsv} className="btn-gold !py-2.5 text-sm" data-testid="export-csv-button"><Download size={15} /> CSV</button>
        {eventId && <button onClick={() => setConfirmDel(eventId)} className="btn-outline !py-2.5 text-sm !text-red-400 !border-red-500/40" data-testid="delete-ticket-data-button"><Trash2 size={15} /> Delete Ticket Data</button>}
      </div>

      <div className="overflow-x-auto rounded-lg border border-[#2A2A32]">
        <table className="w-full min-w-[800px] text-left text-sm" data-testid="bookings-table">
          <thead className="bg-[#0A0A0C] text-xs uppercase tracking-wider text-zinc-500">
            <tr>
              {["Booking ID", "Name", "Mobile", "Email", "Category", "Qty", "Amount", "Payment", "Ticket"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-zinc-500">Loading...</td></tr>
            ) : bookings.length ? bookings.map((b) => (
              <tr key={b.booking_id} className="border-t border-[#2A2A32] hover:bg-[#0A0A0C]/50" data-testid={`booking-row-${b.booking_id}`}>
                <td className="px-4 py-3 font-mono text-xs text-[#E5C158]">{b.booking_id}</td>
                <td className="px-4 py-3">{b.first_name}</td>
                <td className="px-4 py-3">{b.mobile}</td>
                <td className="px-4 py-3 text-zinc-400">{b.email || "-"}</td>
                <td className="px-4 py-3">{b.category_name}</td>
                <td className="px-4 py-3">{b.quantity}</td>
                <td className="px-4 py-3">₹{b.amount}</td>
                <td className="px-4 py-3"><Badge ok={b.payment_status === "paid"} text={b.payment_status} /></td>
                <td className="px-4 py-3"><Badge ok={b.ticket_status === "valid"} warn={b.ticket_status === "used"} text={b.ticket_status} /></td>
              </tr>
            )) : <tr><td colSpan={9} className="px-4 py-8 text-center text-zinc-500">No bookings found.</td></tr>}
          </tbody>
        </table>
      </div>

      {confirmDel && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/80 p-4" data-testid="delete-confirm-modal">
          <div className="card-dark max-w-md p-8 text-center">
            <h3 className="font-serif text-xl font-bold text-red-400">Delete Ticket Data?</h3>
            <p className="mt-3 text-sm text-zinc-400">This deletes all booking & customer transaction data for this event. The event, photos and artists are kept. This cannot be undone.</p>
            <div className="mt-6 flex gap-3">
              <button onClick={deleteTicketData} className="btn-gold flex-1 !from-red-500 !to-red-600" style={{ background: "linear-gradient(to right,#ef4444,#dc2626)" }} data-testid="confirm-delete-button">DELETE</button>
              <button onClick={() => setConfirmDel(null)} className="btn-outline flex-1" data-testid="cancel-delete-button">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Badge = ({ ok, warn, text }) => (
  <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${warn ? "bg-amber-500/15 text-amber-400" : ok ? "bg-green-500/15 text-green-400" : "bg-zinc-700/40 text-zinc-400"}`}>{text}</span>
);
